import { Button, EButtonColor, HeroSection, Section, SectionSubtitle, SectionTitle } from '@web_opxp/components';
import { AuthSelectors, LanguageSelectors, StatsSelectors, useTranslation } from '@web_opxp/features';
import * as React from 'react';
import { connect } from 'react-redux';
import { SKELETON_CLASSNAME, SKELETON_DUMMYTEXT, TRAINING_LED } from '../../base/constants';
import * as HistoryService from '../../base/historyService';
import { CyclesSelectors } from '../../cycles';
import { IState } from '../../store/state';
import { TrainingIntensityRoute } from '../AssessmentPage';
import { AssessmentProgress, ProgressPageState } from '../AssessmentProgress';
import * as Selectors from '../selectors';
import { AssessmentActions } from '../slice';
import './TrainingLedSubpage.scss';

export interface ITrainingLedSubpageProps {
  loading: boolean;
  saving: boolean;
  hasCFQ: boolean;
  authorized: boolean;
  started: boolean;
  selectedLed?: number;
  trackActivateLed: () => void;
  trackDeactivateLed: () => void;
  updateTrainingLed: (activate: 0 | 1) => void;
  onStart: () => void;
}

export const TrainingLedSubpageFC: React.FC<ITrainingLedSubpageProps> = ({
  loading,
  saving,
  hasCFQ,
  authorized,
  started,
  selectedLed,
  trackActivateLed,
  trackDeactivateLed,
  updateTrainingLed,
  onStart,
}) => {
  React.useEffect(() => {
    if (!authorized || started) {
      return;
    }
    onStart();
  });

  // If schedule is already selected, go to Peer group
  if (selectedLed !== undefined) {
    HistoryService.redirectInternal(TrainingIntensityRoute);
  }

  const handleActivate = () => {
    updateTrainingLed(0);
    trackActivateLed();
    HistoryService.redirectInternal(TrainingIntensityRoute);
  };

  const handleDeactivate = () => {
    updateTrainingLed(1);
    trackDeactivateLed();
    HistoryService.redirectInternal(TrainingIntensityRoute);
  };

  const { t } = useTranslation();

  const title = !loading ? t('postreg:TrainingLED.Title') : SKELETON_DUMMYTEXT;
  const subtitle = !loading ? t('postreg:TrainingLED.Description') : SKELETON_DUMMYTEXT;
  const skeletonClassname = loading ? SKELETON_CLASSNAME : undefined;
  const activateLabel = !loading ? t('postreg:TrainingLED.Activate') : SKELETON_DUMMYTEXT;
  const deactivateLabel = !loading ? t('postreg:TrainingLED.Deactivate') : SKELETON_DUMMYTEXT;

  return (
    <>
      <HeroSection lottie="Scientist" className="training-led">
        <SectionTitle className={skeletonClassname}>{title}</SectionTitle>
        <SectionSubtitle className={skeletonClassname}>{subtitle}</SectionSubtitle>
        <div className="buttons-container">
          <Button
            color={EButtonColor.white}
            disabled={loading || saving}
            label={activateLabel}
            onClick={handleActivate}
          />
          <Button
            color={EButtonColor.white}
            disabled={loading || saving}
            label={deactivateLabel}
            onClick={handleDeactivate}
          />
        </div>
      </HeroSection>
      <Section>
        <AssessmentProgress page={ProgressPageState.EvaluationDone} cfq={hasCFQ} />
      </Section>
    </>
  );
};

export const mapStateToProps = (state: IState) => {
  const loading = LanguageSelectors.isLoading(state);
  const saving = Selectors.getSavingState(state);
  const hasCFQ = CyclesSelectors.doesCurrentSessionHasCFQWorkouts(state);
  const authorized = AuthSelectors.isAuthorized(state);
  const started = Selectors.isAssessmentStarted(state);
  const selectedLed = StatsSelectors.getStatIntValueById(state, TRAINING_LED);
  return { loading, saving, hasCFQ, authorized, started, selectedLed };
};

export const mapDispatchToProps = {
  trackActivateLed: AssessmentActions.trainingLedActivate,
  trackDeactivateLed: AssessmentActions.trainingLedDeactivate,
  updateTrainingLed: AssessmentActions.trainingLedUpdate,
  onStart: AssessmentActions.initAssessment,
};

const TrainingLedSubpage = connect(mapStateToProps, mapDispatchToProps)(TrainingLedSubpageFC);
export default TrainingLedSubpage;
